/* src/components/NotificationPrefs.js - v1.1 - Added Nexus AI Opt-Out
   CHANGELOG (v1.0 -> v1.1): Added a "Receive Messages from Nexus AI"
   toggle, backed by the separate `nexus_opt_out` user field (NOT part of
   the notification_prefs category system - it gates Nexus's proactive
   outreach in services/nexus_ai.py's run_interaction_cycle() and
   run_inactivity_reminder_sweep(), both in-app message and email in one
   flag, so it's a single checkbox rather than an Email/Push pair). Found
   missing from this page 2026-09-10 while investigating a user's
   unsubscribe complaint - the same investigation also caught
   run_inactivity_reminder_sweep() not checking nexus_opt_out at all
   (fixed separately in nexus_ai.py).

   v1.0 notes:
   Linked from the "Manage email preferences" footer that now appears on
   every outgoing user-facing email (see utils.py's send_email()). Reached
   via a stable per-user token (?token=...) so it works without logging
   in - the same reason ResetPassword.js exists as a standalone page
   rather than something behind the app's normal auth wall.

   Only the 4 non-account categories (likes_comments, follows,
   following_posts, group_posts) are editable here, for both Email and
   Push - Push is included even though the authenticated Profile.js
   settings modal only exposes Email, since this single web page is also
   the mobile app's unsubscribe destination (email links always open in a
   browser, even from within the app) and both fields live on the same
   server-side notification_prefs record regardless of which client
   toggles them.

   'admin_alerts' (Account & Security) is always shown locked/checked -
   per explicit decision, those notifications can't be turned off from
   here. A user who doesn't want them doesn't want the account; the copy
   points them at logging in to delete it instead of offering a mute. */
import React, { useState, useEffect } from 'react';

const EDITABLE_CATEGORIES = ['likes_comments', 'follows', 'following_posts', 'group_posts'];

// Matches utils.py's DEFAULT_NOTIFICATION_PREFS / Profile.js's
// NOTIF_PREF_LABELS exactly, so the wording is consistent everywhere a
// user sees these categories.
const NOTIF_PREF_LABELS = {
    likes_comments: 'Likes & comments on your posts',
    follows: 'New followers',
    following_posts: 'Posts from people you follow',
    group_posts: 'Posts in your communities',
    admin_alerts: 'Account & admin alerts',
};

function NotificationPrefs({ onComplete, backgroundStyle }) {
    const [token, setToken] = useState('');
    const [username, setUsername] = useState(null);
    const [prefs, setPrefs] = useState(null);
    const [nexusOptOut, setNexusOptOut] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const t = urlParams.get('token');
        setToken(t);

        if (!t) {
            setError('No link token provided.');
            setLoading(false);
            return;
        }

        fetch(`/api/notification-prefs?token=${encodeURIComponent(t)}`)
            .then(res => res.json().then(data => ({ ok: res.ok, data })))
            .then(({ ok, data }) => {
                if (ok) {
                    setUsername(data.username);
                    setPrefs(data.prefs);
                    setNexusOptOut(!!data.nexus_opt_out);
                } else {
                    setError(data.error || 'This link is invalid or has expired.');
                }
            })
            .catch(() => setError('Connection error.'))
            .finally(() => setLoading(false));
    }, []);

    const toggle = (category, channel) => {
        setPrefs(prev => ({
            ...prev,
            [category]: { ...prev[category], [channel]: !prev[category][channel] }
        }));
        setSaved(false);
    };

    const toggleNexus = () => {
        setNexusOptOut(prev => !prev);
        setSaved(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {};
            EDITABLE_CATEGORIES.forEach(cat => { payload[cat] = prefs[cat]; });
            const res = await fetch('/api/notification-prefs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, prefs: payload, nexus_opt_out: nexusOptOut })
            });
            if (res.ok) {
                setSaved(true);
            } else {
                const data = await res.json();
                alert(data.error || 'Failed to save preferences.');
            }
        } catch (err) {
            alert('Connection error - please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 20px', ...backgroundStyle }}>
            <div className="card" style={{ width: '100%', maxWidth: '480px', padding: '40px', textAlign: 'left' }}>

                <h2 style={{ marginBottom: '5px', textAlign: 'center' }}>Notification Preferences</h2>

                {loading && <p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p>}

                {error && (
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ color: '#ff4757', marginTop: '20px', marginBottom: '20px' }}>{error}</p>
                        <button onClick={onComplete} className="action-btn">Return to Nubix</button>
                    </div>
                )}

                {!loading && !error && username && (
                    <>
                        <p style={{ color: '#aaa', fontSize: '0.9rem', textAlign: 'center', marginBottom: '30px' }}>
                            Managing preferences for <strong style={{ color: 'white' }}>{username}</strong>
                        </p>

                        <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '10px' }}>
                            EMAIL &amp; PUSH NOTIFICATIONS
                        </label>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <div style={{ flex: 1 }} />
                                <div style={{ width: '60px', textAlign: 'center', fontSize: '0.75rem', color: '#888' }}>Email</div>
                                <div style={{ width: '60px', textAlign: 'center', fontSize: '0.75rem', color: '#888' }}>Push</div>
                            </div>

                            {EDITABLE_CATEGORIES.map(category => (
                                <div key={category} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <div style={{ flex: 1, fontSize: '0.9rem' }}>{NOTIF_PREF_LABELS[category]}</div>
                                    <div style={{ width: '60px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={prefs[category]?.email ?? true}
                                            onChange={() => toggle(category, 'email')}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                    </div>
                                    <div style={{ width: '60px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={prefs[category]?.push ?? true}
                                            onChange={() => toggle(category, 'push')}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                    </div>
                                </div>
                            ))}

                            <div style={{ display: 'flex', alignItems: 'center', padding: '10px 0', opacity: 0.5 }}>
                                <div style={{ flex: 1, fontSize: '0.9rem' }}>{NOTIF_PREF_LABELS.admin_alerts}</div>
                                <div style={{ width: '60px', textAlign: 'center' }}>
                                    <input type="checkbox" checked disabled style={{ width: '18px', height: '18px' }} />
                                </div>
                                <div style={{ width: '60px', textAlign: 'center' }}>
                                    <input type="checkbox" checked disabled style={{ width: '18px', height: '18px' }} />
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '25px', display: 'flex', alignItems: 'center', padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                            <div style={{ flex: 1, fontSize: '0.9rem' }}>Receive Messages from Nexus AI</div>
                            <div style={{ width: '60px', textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={!nexusOptOut}
                                    onChange={toggleNexus}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                            </div>
                        </div>

                        <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '10px', lineHeight: 1.5 }}>
                            Account &amp; admin alerts can't be turned off here - these cover things like email verification,
                            security notices, and moderation, and are required to operate your account. If you no longer
                            want to receive any communication from Nubix, you can log in and delete your account from your
                            Profile settings.
                        </p>

                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={{ marginTop: '25px', width: '100%', padding: '12px', background: saved ? '#2ed573' : 'linear-gradient(45deg, #ff4757, #ff6b81)', color: 'white', border: 'none', borderRadius: '30px', fontWeight: 'bold', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}
                        >
                            {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save Preferences'}
                        </button>

                        <button
                            type="button"
                            onClick={onComplete}
                            style={{ width: '100%', padding: '12px', marginTop: '10px', background: 'transparent', border: 'none', color: '#aaa', cursor: 'pointer' }}
                        >
                            Return to Nubix
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export default NotificationPrefs;
